/**
 * The card beside a step the pointer rests on: the step's quick options (in
 * the editor) or how it went (on a run), with a gear that opens the step's
 * full panel. It sits in the chart, to the right of the step, at the same
 * size whatever the zoom. A double click still opens the panel directly.
 *
 * @module components/workflows/canvas/hoverCard
 */
import type { WorkflowNode } from "@amoreg/contracts";
import { SettingsIcon } from "lucide-react";
import type { ReactNode } from "react";

import { cn } from "~/lib/utils";
import { Button } from "../../ui/button";
import { NODE_WIDTH } from "./geometry";
import { KINDS, kindClass } from "./kinds";
import { GLASS_CARD } from "./panels";

/** How long the pointer rests on a step before its card opens, and lingers off it before it closes. */
export const HOVER_OPEN_MS = 300;
export const HOVER_CLOSE_MS = 200;
/** The card's width and its gap from the step, in screen pixels. */
export const HOVER_CARD_WIDTH = 320;
const HOVER_GAP = 12;

export type HoverCardSide = "right" | "left" | "below";

/**
 * Where the card goes, all in screen pixels across the canvas: right of the
 * step if it fits before `rightLimit` (the canvas edge or an open panel),
 * else left of it if it clears `leftLimit` (the Steps panel), else below it.
 */
export function hoverCardSide(stepLeft: number, stepRight: number, leftLimit: number, rightLimit: number): HoverCardSide {
  if (stepRight + HOVER_GAP + HOVER_CARD_WIDTH <= rightLimit) return "right";
  if (stepLeft - HOVER_GAP - HOVER_CARD_WIDTH >= leftLimit) return "left";
  return "below";
}

function cardPosition(node: WorkflowNode, height: number, zoom: number, side: HoverCardSide): string {
  if (side === "right") return `${node.position.x + NODE_WIDTH + HOVER_GAP / zoom}px ${node.position.y}px`;
  if (side === "left") return `${node.position.x - (HOVER_CARD_WIDTH + HOVER_GAP) / zoom}px ${node.position.y}px`;
  return `${node.position.x}px ${node.position.y + height + HOVER_GAP / zoom}px`;
}

export function StepHoverCard({
  node,
  zoom,
  side,
  height,
  onMore,
  onPointerEnter,
  onPointerLeave,
  onEngage,
  children,
}: {
  readonly node: WorkflowNode;
  readonly zoom: number;
  readonly side: HoverCardSide;
  /** The step's drawn height, for a card below it. */
  readonly height: number;
  /** Opens the step's full panel. */
  readonly onMore: () => void;
  readonly onPointerEnter: () => void;
  readonly onPointerLeave: () => void;
  /** Something in the card was pressed or focused: it stays open until the person is done. */
  readonly onEngage: () => void;
  readonly children: ReactNode;
}) {
  const meta = KINDS[node.type];
  const Icon = meta.icon;
  return (
    <div
      role="dialog"
      aria-label={`${meta.label}: ${node.label}`}
      data-canvas-overlay
      data-workflow-hover-card={node.id}
      onPointerDown={(event) => {
        event.stopPropagation();
        onEngage();
      }}
      onFocus={onEngage}
      onDoubleClick={(event) => event.stopPropagation()}
      onPointerEnter={onPointerEnter}
      onPointerLeave={onPointerLeave}
      style={{
        translate: cardPosition(node, height, zoom, side),
        scale: `${1 / zoom}`,
        width: HOVER_CARD_WIDTH,
      }}
      className={cn(
        kindClass(node.type),
        GLASS_CARD,
        "absolute top-0 left-0 z-30 origin-top-left cursor-default p-3 select-text transition-opacity duration-150 ease-power3-out starting:opacity-0",
      )}
    >
      <div className="mb-2.5 flex items-center gap-2">
        <Icon aria-hidden className="size-4 shrink-0 text-kind" />
        <span className="min-w-0 flex-1 truncate text-[13px] font-semibold text-foreground">{node.label}</span>
        <Button size="icon-xs" variant="ghost" aria-label="More options" title="More options" onClick={onMore} data-workflow-hover-more="">
          <SettingsIcon />
        </Button>
      </div>
      {children}
    </div>
  );
}
